// Shared markdown styles for consistent styling across components

// Azure DevOps sometimes emits changelog markdown with unencoded spaces in link
// destinations (e.g. ".../Business Capabilities/_git/..."). CommonMark requires
// link destinations containing whitespace to be percent-encoded or wrapped in
// <...>, otherwise the parser treats the whole thing as plain text. This
// normalizes such links before handing the markdown to a renderer.
export function sanitizeMarkdownLinkUrls(markdown: string): string {
  if (!markdown) {
    return markdown;
  }
  return markdown.replace(/\]\(([^)]+)\)/g, (match, url) => {
    if (/\s/.test(url)) {
      return `](${url.replace(/ /g, '%20')})`;
    }
    return match;
  });
}

export const createMarkdownStyles = (theme: any) => ({
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  '& h1': {
    fontSize: '1.5rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(0.5),
  },
  '& h2': {
    fontSize: '1.25rem',
  },
  '& h3': {
    fontSize: '1.1rem',
  },
  '& p': {
    marginBottom: theme.spacing(1),
    lineHeight: 1.6,
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
      color: theme.palette.primary.dark,
      textDecoration: 'underline',
    },
    '&:visited': {
      color: theme.palette.primary.dark,
    },
  },
  '& ul, & ol': {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  '& li': {
    marginBottom: theme.spacing(0.5),
  },
  '& code': {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    padding: '2px 4px',
    borderRadius: '3px',
    fontFamily: 'monospace',
    fontSize: '0.9em',
  },
  '& pre': {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    padding: theme.spacing(1),
    borderRadius: '4px',
    overflow: 'auto',
    marginBottom: theme.spacing(1),
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    marginLeft: 0,
    paddingLeft: theme.spacing(2),
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5, 1),
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(2, 0),
  },
}); 